import useUpdateEffect from '@restart/hooks/useUpdateEffect';
import classNames from 'classnames/bind';
import React, { useState, useRef, useContext } from 'react';
import { RiCheckLine, RiCloseLine } from 'react-icons/ri';
import { useStore } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import styles from './SelectProject.module.scss';

import { PrimaryButton, FunctionButton } from '@/components/ui/kit/Button';
import { IconButton } from '@/components/ui/kit/Button';
import { FieldText } from '@/components/ui/kit/Field';
import { MissionTableContent } from '@/components/ui/MissionTable';
import { MissionTableProvider, MissionTableContext } from '@/components/ui/MissionTable/MissionTableContext';
import ModalWrapper from '@/components/ui/ModalWrapper';
import { createProject, createMission, updateMission } from '@/helpers/Requester';
import { modalManager } from '@/libs/ModalContainer';
import { NotifierService as notifier } from '@/libs/Notifier';

const cx = classNames.bind(styles);

const SelectProjectContent = () => {
  const store = useStore();
  const navigate = useNavigate();
  const { load, projects, selectedProjectId, selectedMissionId } = useContext(MissionTableContext);
  const [isCreatingProject, setCreatingProject] = useState(false);
  const [missionName, setMissionName] = useState('');
  const projectNameFieldRef = useRef();

  useUpdateEffect(() => {
    setMissionName('');
  }, [selectedProjectId, selectedMissionId]);

  useUpdateEffect(() => {
    if (isCreatingProject) {
      projectNameFieldRef.current.focus();
    }
  }, [isCreatingProject]);

  const doCreateProject = () => {
    const name = projectNameFieldRef.current.value.trim();
    if (name === '') {
      notifier.error('Please enter the project name.');
      return;
    }

    createProject(name).then(({ success }) => {
      if (!success) {
        notifier.error('Failed to create the project. Please try again.');
        return;
      }

      projectNameFieldRef.current.value = '';
      setCreatingProject(false);
      load();
    });
  };

  const close = () => {
    modalManager.hide();
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.stopPropagation();
      doCreateProject();
    } else if (e.key === 'Escape') {
      e.stopPropagation();
      setCreatingProject(false);
    }
  };

  const handleSuccess = (missionId) => {
    setMissionName('');
    navigate(`/mission-hub/${missionId}`, { replace: true });
    notifier.success('The mission has been saved.');
    close();
  };

  const handleFailure = (errorCode) => {
    if (errorCode === 2001) {
      notifier.error('The mission name already exists in this project.');
    }
  };

  const doSave = () => {
    const { json } = store.getState().mission;

    if (selectedProjectId) {
      if (missionName.trim() === '') {
        notifier.error('Please enter the mission name.');
        return;
      }

      createMission(selectedProjectId, missionName.trim(), json).then(({ success, data }) => {
        success ? handleSuccess(data.id) : handleFailure(data.code);
      });
      return;
    }

    if (selectedMissionId) {
      let selectedMission = null;
      for (const project of projects) {
        selectedMission = project.missions.find(({ id }) => id === selectedMissionId);
        if (selectedMission) break;
      }

      if (selectedMission) {
        updateMission(selectedMission.id, selectedMission.name, json).then(({ success, data }) => {
          success ? handleSuccess(selectedMission.id) : handleFailure(data.code);
        });
        return;
      }
    }

    notifier.error('Please select a project or a mission to overwrite.');
  };

  return (
    <ModalWrapper className={cx('container')}>
      <div className={cx('wrapper')}>
        <div className={cx('header')}>
          <div className={cx('message')}>Select a project to save the mission.</div>
          {!isCreatingProject && <FunctionButton text="New Project" onClick={() => setCreatingProject(true)} />}
        </div>
        {isCreatingProject && (
          <div className={cx('projectForm')}>
            <FieldText
              ref={projectNameFieldRef}
              placeholder="Project name"
              maxLength={24}
              onKeyDown={handleKeyDown}
              className={cx('field')}
            />
            <div className={cx('actions')}>
              <IconButton type="mini" onClick={doCreateProject} Icon={RiCheckLine} />
              <IconButton type="mini" onClick={() => setCreatingProject(false)} Icon={RiCloseLine} />
            </div>
          </div>
        )}
        <div className={cx('table')}>
          <MissionTableContent />
          {selectedProjectId && (
            <FieldText
              value={missionName}
              placeholder="Mission name"
              maxLength={24}
              onChange={(e) => setMissionName(e.target.value)}
              className={cx('field')}
            />
          )}
        </div>
      </div>
      <div className={cx('buttons')}>
        <PrimaryButton text="Cancel" onClick={close} />
        <PrimaryButton text="Save" accent onClick={doSave} />
      </div>
    </ModalWrapper>
  );
};

const SelectProject = () => {
  return (
    <MissionTableProvider onSelectProject={() => {}} onSelectMission={() => {}} showMissionEdit={false}>
      <SelectProjectContent />
    </MissionTableProvider>
  );
};

export default SelectProject;
